import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Paper,
  Tabs,
  Tab,
  Fab,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  Visibility as PreviewIcon,
  MoreVert as MoreVertIcon,
  Web as TemplateIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../contexts/AuthContext';
import {
  getTemplates,
  deleteTemplate,
  duplicateTemplate,
  createTemplate,
  updateTemplate,
  CMSTemplate,
  TemplateLayoutSettings,
  CMSSection,
  initializeBuiltInTemplates,
} from '../../../../firebase/services/cmsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TemplateManager: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<CMSTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<CMSTemplate[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<CMSTemplate | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, tabValue]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      // Initialize built-in templates if they don't exist
      if (userProfile?.uid) {
        await initializeBuiltInTemplates(userProfile.uid);
      }

      const allTemplates = await getTemplates();
      setTemplates(allTemplates);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Filter by category based on tab
    if (tabValue === 1) {
      filtered = filtered.filter(t => t.category === 'built-in');
    } else if (tabValue === 2) {
      filtered = filtered.filter(t => t.category === 'custom');
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: CMSTemplate) => {
    setMenuAnchor(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTemplate(null);
  };

  const handleEdit = () => {
    if (selectedTemplate) {
      navigate(`/admin/business/online/templates/${selectedTemplate.id}/edit`);
    }
    handleMenuClose();
  };

  const handleDuplicate = () => {
    if (selectedTemplate) {
      setNewTemplateName(`${selectedTemplate.name} Copy`);
      setDuplicateDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handlePreview = () => {
    if (selectedTemplate) {
      navigate(`/admin/business/online/templates/${selectedTemplate.id}/preview`);
    }
    handleMenuClose();
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedTemplate || !userProfile?.uid) return;

    try {
      await duplicateTemplate(selectedTemplate.id, newTemplateName, userProfile.uid);
      setSuccess('Template duplicated successfully');
      setDuplicateDialogOpen(false);
      setNewTemplateName('');
      await loadTemplates();
    } catch (err: any) {
      console.error('Error duplicating template:', err);
      setError(err.message || 'Failed to duplicate template');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTemplate(selectedTemplate.id);
      setSuccess('Template deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
      await loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      setError(err.message || 'Failed to delete template');
    }
  };

  const getTemplatePreview = (template: CMSTemplate) => {
    // Generate a simple preview based on sections
    const sectionTypes = template.sections.map(s => s.type);
    const previewText = sectionTypes.join(' + ');
    return previewText;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            <TemplateIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Page Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage reusable page templates for your website
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/business/online/templates/new"
          size="large"
        >
          Create Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label={`All (${templates.length})`} />
                <Tab label={`Built-in (${templates.filter(t => t.category === 'built-in').length})`} />
                <Tab label={`Custom (${templates.filter(t => t.category === 'custom').length})`} />
              </Tabs>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Templates Grid */}
      <TabPanel value={tabValue} index={0}>
        <TemplateGrid templates={filteredTemplates} onMenuOpen={handleMenuOpen} />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <TemplateGrid 
          templates={filteredTemplates.filter(t => t.category === 'built-in')} 
          onMenuOpen={handleMenuOpen} 
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <TemplateGrid 
          templates={filteredTemplates.filter(t => t.category === 'custom')} 
          onMenuOpen={handleMenuOpen} 
        />
      </TabPanel>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuList>
          <MenuItemComponent onClick={handlePreview}>
            <ListItemIcon>
              <PreviewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Preview</ListItemText>
          </MenuItemComponent>
          <MenuItemComponent onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItemComponent>
          <MenuItemComponent onClick={handleDuplicate}>
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate</ListItemText>
          </MenuItemComponent>
          {selectedTemplate && !selectedTemplate.isSystem && (
            <MenuItemComponent onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItemComponent>
          )}
        </MenuList>
      </Menu>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedTemplate?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
        <DialogTitle>Duplicate Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Template Name"
            fullWidth
            variant="outlined"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDuplicateConfirm}
            variant="contained"
            disabled={!newTemplateName.trim()}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface TemplateGridProps {
  templates: CMSTemplate[];
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, template: CMSTemplate) => void;
}

const TemplateGrid: React.FC<TemplateGridProps> = ({ templates, onMenuOpen }) => {
  if (templates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <TemplateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No templates found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create your first template to get started
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/business/online/templates/new"
        >
          Create Template
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {templates.map((template) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
          <TemplateCard template={template} onMenuOpen={onMenuOpen} />
        </Grid>
      ))}
    </Grid>
  );
};

interface TemplateCardProps {
  template: CMSTemplate;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, template: CMSTemplate) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onMenuOpen }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/admin/business/online/templates/${template.id}/preview`);
  };

  const getTemplateIcon = (category: string) => {
    return category === 'built-in' ? '🏗️' : '🎨';
  };

  const getSectionTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      hero: '🦸',
      text: '📝',
      image_text: '🖼️',
      gallery: '🖼️',
      services: '⚙️',
      team: '👥',
      timeline: '📅',
      contact_info: '📞',
      cta: '🎯',
      custom: '🔧',
    };
    return icons[type] || '📄';
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      {/* Template Preview */}
      <CardMedia
        sx={{
          height: 200,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
        }}
        onClick={handleCardClick}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            {getTemplateIcon(template.category)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            {template.sections.slice(0, 4).map((section) => (
              <Typography key={section.id} variant="h6">
                {getSectionTypeIcon(section.type)}
              </Typography>
            ))}
            {template.sections.length > 4 && (
              <Typography variant="body2" color="text.secondary">
                +{template.sections.length - 4}
              </Typography>
            )}
          </Box>
        </Box>
        
        {template.isSystem && (
          <Chip
            label="System"
            size="small"
            color="primary"
            sx={{ position: 'absolute', top: 8, left: 8 }}
          />
        )}
      </CardMedia>

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            {template.name}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen(e, template);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {template.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {template.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={template.category === 'built-in' ? 'Built-in' : 'Custom'}
            size="small"
            variant="outlined"
            color={template.category === 'built-in' ? 'primary' : 'secondary'}
          />
          <Chip
            label={`${template.sections.length} sections`}
            size="small"
            variant="outlined"
          />
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        <Button
          size="small"
          startIcon={<PreviewIcon />}
          onClick={handleCardClick}
        >
          Preview
        </Button>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/business/online/templates/${template.id}/edit`)}
        >
          Edit
        </Button>
      </CardActions>
    </Card>
  );
};

export default TemplateManager; 